/**
 * Escuta novas mensagens da organização via PartyKit (som + lista).
 * Falhas são silenciosas — realtime opcional; chat usa polling como fallback.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { canReceiveAssignedNotification } from '../../../domain/support/guards';
import { useAppSounds } from '../../../hooks/useAppSounds';
import { useNotifications } from '../../../hooks/useNotifications';
import { createModuleLogger } from '../../../utils/logger';
import { useAppContext } from '../../../contexts/AppContext';
import {
    PARTYKIT_CONNECTION_DEBOUNCE_MS,
    canUsePartykit,
    isValidPartykitHost,
    claimAuthedPartySocket,
    connectPartySocket,
    safeClosePartySocket,
    attachPartykitVisibilityReconnect,
    createPartykitCooldownController,
    attachPartySocketHandlers,
    emitPartyAssignmentChanged,
} from '../utils/partykitSocket';
import type PartySocket from 'partysocket';

const log = createModuleLogger('[GlobalNotification]');
const SOUND_THROTTLE_MS = 1000;

interface GlobalMessage {
    type: 'CONNECTED' | 'NEW_MESSAGE_GLOBAL' | 'CONNECTION_STATUS_CHANGE' | 'CONVERSATION_CHANGED';
    conversationId?: string;
    messageId?: string;
    organizationId?: string;
    assignedTo?: string | null;
    phone?: string;
    status?: string;
    unreadCount?: number;
    remoteJid?: string;
    connectionId?: string;
    timestamp?: string;
    fromMe?: boolean;
}

let hasLoggedDisabled = false;

function logPollingFallbackOnce() {
    if (hasLoggedDisabled) return;
    log.debug('PartyKit Global indisponível — usando polling');
    hasLoggedDisabled = true;
}

function dispatchChatReceived(detail: GlobalMessage) {
    window.dispatchEvent(new CustomEvent('chat:message_received', { detail }));
}

/** Catch-up após reconnect/PWA — eventos perdidos enquanto a aba estava suspensa. */
export function dispatchPartykitCatchUp(organizationId: string) {
    if (!organizationId) return;
    dispatchChatReceived({ type: 'CONVERSATION_CHANGED', organizationId });
}

export function useGlobalMessageNotification() {
    const { currentPage } = useAppContext();
    const isChatActive = currentPage === 'chat';
    const socketRef = useRef<PartySocket | null>(null);
    const lastSoundTimeRef = useRef(0);
    const { user } = useAuth();
    const { currentOrganization } = useOrganization();
    const { playSound } = useAppSounds();
    const { sendNotification } = useNotifications();

    const playSoundRef = useRef(playSound);
    const sendNotificationRef = useRef(sendNotification);
    const userIdRef = useRef(user?.id);
    const roleRef = useRef(currentOrganization?.role);
    playSoundRef.current = playSound;
    sendNotificationRef.current = sendNotification;
    userIdRef.current = user?.id;
    roleRef.current = currentOrganization?.role;

    const disconnect = useCallback(() => {
        safeClosePartySocket(socketRef.current);
        socketRef.current = null;
    }, []);

    const handlePayload = useCallback((data: GlobalMessage) => {
        if (data.type === 'CONNECTED') return;

        if (data.type === 'CONVERSATION_CHANGED') {
            emitPartyAssignmentChanged(data);
            dispatchChatReceived(data);
            return;
        }

        if (data.type !== 'NEW_MESSAGE_GLOBAL') return;
        dispatchChatReceived(data);

        if (data.fromMe) return;
        if (!canReceiveAssignedNotification(data.assignedTo, userIdRef.current, roleRef.current)) return;
        const now = Date.now();
        if (now - lastSoundTimeRef.current <= SOUND_THROTTLE_MS) return;

        playSoundRef.current('message');
        lastSoundTimeRef.current = now;
        if (document.hidden) {
            sendNotificationRef.current('Nova mensagem', {
                body: 'Você recebeu uma nova mensagem',
                tag: `new-msg-${data.conversationId}`,
            });
        }
    }, []);

    useEffect(() => {
        if (!isChatActive || !isValidPartykitHost() || !currentOrganization?.id) {
            disconnect();
            return;
        }

        let cancelled = false;
        let connectAttempt = 0;
        const orgId = currentOrganization.id;
        const roomId = `org-${orgId}`;

        const connect = () => {
            if (cancelled) return;
            if (!canUsePartykit('status')) {
                logPollingFallbackOnce();
                return;
            }

            const attempt = ++connectAttempt;
            safeClosePartySocket(socketRef.current);
            socketRef.current = null;

            void (async () => {
                try {
                    const socket = await claimAuthedPartySocket(roomId, 'status', {
                        isCancelled: () => cancelled,
                        attempt,
                        latestAttempt: () => connectAttempt,
                    });
                    if (!socket) return;
                    socketRef.current = socket;
                    attachPartySocketHandlers(socket, 'status', () => cancelled, {
                        onOpen: () => {
                            hasLoggedDisabled = false;
                            // Equivalente ao SUBSCRIBED do Realtime: recupera o que a PWA perdeu.
                            dispatchPartykitCatchUp(orgId);
                        },
                        onClose: () => {
                            if (!canUsePartykit('status')) logPollingFallbackOnce();
                        },
                        armCooldown: () => cooldown.armAfterFailure(),
                        onMessage: (raw) => {
                            try {
                                handlePayload(JSON.parse(raw) as GlobalMessage);
                            } catch (error) {
                                log.error('Erro ao processar mensagem global:', error);
                            }
                        },
                    });
                    connectPartySocket(socket);
                } catch (error) {
                    log.debug('PartyKit Global não inicializado:', error);
                }
            })();
        };

        const cooldown = createPartykitCooldownController('status', connect, () => cancelled);
        const debounceTimer = setTimeout(connect, PARTYKIT_CONNECTION_DEBOUNCE_MS);
        const detachVisibility = attachPartykitVisibilityReconnect(
            'status', () => socketRef.current, connect, () => cancelled,
        );

        // Socket “zumbi” após suspend: readyState ainda OPEN, mas eventos se perderam.
        const onVisible = () => {
            if (cancelled || document.visibilityState !== 'visible') return;
            dispatchPartykitCatchUp(orgId);
        };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            cancelled = true;
            clearTimeout(debounceTimer);
            cooldown.dispose();
            detachVisibility();
            document.removeEventListener('visibilitychange', onVisible);
            disconnect();
        };
    }, [isChatActive, currentOrganization?.id, disconnect, handlePayload]);
}
